import React, { useEffect, useState } from 'react'

import { Grid, Col, Display, Visible, ScreenReaderLiveRegion } from '@gousto-internal/citrus-react'
import { DiscountAppliedBar } from 'DiscountAppliedBar/DiscountAppliedBar'
import { useDiscountAppliedData } from 'DiscountAppliedBar/discountAppliedBarHooks'
import { useDispatch, useSelector } from 'react-redux'
import { usePrevious } from 'react-use'

import { redirect } from 'actions/redirect'
import { trackUTMAndPromoCode } from 'actions/tracking'
import { closeDiscountAppliedBar } from 'actions/trackingKeys'
import routesConfig from 'config/routes'
import { BoxSummaryDrawer } from 'routes/Checkout/Components/BoxSummaryDrawer/BoxSummaryDrawer'
import { CheckoutStepContainer } from 'routes/Checkout/Components/CheckoutStepContainer/CheckoutStepContainer'
import { CheckoutUrgency } from 'routes/Checkout/Components/CheckoutUrgency/CheckoutUrgency'
import { LoginModal } from 'routes/Checkout/Components/LoginModal'
import {
  clearPayPalClientToken,
  fetchGoustoRef,
  trackSignupPageChange,
  trackApplePayEligibility,
} from 'routes/Checkout/checkoutActions'
import {
  Breadcrumbs as BreadcrumbsEnum,
  CHECKOUT_STEPS,
  CheckoutStepIds,
} from 'routes/Checkout/checkoutConfig'
import { useIsApplePayEligible } from 'routes/Checkout/hooks/useIsApplePayEnabled'
import {
  signupDismissDiscountAppliedBar,
  signupShowDiscountAppliedBar,
} from 'routes/Signup/signupActions'

import { Breadcrumbs } from './Components/Breadcrumbs'
import { OrderSummaryAndBoxDetails } from './Components/OrderSummaryAndBoxDetails'
import { useCheckoutScript, usePaypalScript, useRedirectOnError } from './checkoutHooks'
import { getIsDiscountAppliedBannerDismissed } from './checkoutSelectors'
import { useIsCheckoutBoxDiscountVisualisationEnabled } from './hooks/useIsCheckoutBoxDiscountVisualisationEnabled'

type CheckoutProps = {
  /**
   * URL params.
   */
  params: {
    /**
     * Step to display in Checkout flow right now.
     */
    stepName?: CheckoutStepIds
  }
}

/**
 * Checkout flow mechanism component.
 * Serves as logic wrapper for all steps in checkout flow.
 * Displays proper checkout step depending on URL.
 */
export const Checkout = ({ params: { stepName: urlStepId } }: CheckoutProps) => {
  const previousUrlStepId = usePrevious(urlStepId)
  const currentStepInfo = CHECKOUT_STEPS.find((checkoutStep) => checkoutStep.id === urlStepId)

  const dispatch = useDispatch()
  const isApplePayEligible = useIsApplePayEligible()
  const isBoxDiscountVisualisationEnabled = useIsCheckoutBoxDiscountVisualisationEnabled()

  const { isDiscountEnabled, discountTip } = useDiscountAppliedData()
  const isDiscountAppliedBannerDismissed = useSelector(getIsDiscountAppliedBannerDismissed)

  useRedirectOnError()

  const { isCheckoutScriptReady, loadCheckoutScript } = useCheckoutScript()
  const { arePaypalScriptsReady, loadPayPalScripts } = usePaypalScript()
  /**
   * Map of Breadcrumb to user-chosen Step.
   * For different breadcrumbs there can be different steps depending on what user clicked, this mapping "freezes" it.
   */
  const [breadcrumbsToStepsMapping, setBreadcrumbsToStepsMapping] = useState<{
    [key in BreadcrumbsEnum]?: CheckoutStepIds
  }>({
    [BreadcrumbsEnum.ACCOUNT]: CheckoutStepIds.ACCOUNT,
  })

  const redirectToStep = (stepId?: CheckoutStepIds): void => {
    if (!stepId) {
      return
    }
    const stepInfo = CHECKOUT_STEPS.find((checkoutStep) => checkoutStep.id === stepId)
    if (!stepInfo) {
      // should not happen, but theoretically can
      return
    }
    const nextStepId = stepInfo.id
    dispatch(trackSignupPageChange(nextStepId))
    dispatch(redirect(`${routesConfig.client['check-out']}/${nextStepId}`)) // ID serves as route
  }

  /**
   * First render. Load everything and redirect to "Account" step.
   */
  useEffect(() => {
    // make sure on reload we get to 1st step (Account)
    if (urlStepId !== CheckoutStepIds.ACCOUNT) {
      redirectToStep(CheckoutStepIds.ACCOUNT)
    }

    loadCheckoutScript()
    loadPayPalScripts()

    dispatch(fetchGoustoRef())
    dispatch(trackApplePayEligibility(isApplePayEligible))

    // show the DiscountAppliedBar component which may have been dismissed in the signup funnel
    if (isDiscountAppliedBannerDismissed) {
      dispatch(signupShowDiscountAppliedBar())
    }

    return () => {
      dispatch(clearPayPalClientToken())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Update "breadcrumbs to steps" mapping if stepId has changed.
   */
  useEffect(() => {
    if (!currentStepInfo) return
    setBreadcrumbsToStepsMapping({
      ...breadcrumbsToStepsMapping,
      [currentStepInfo.breadcrumb]: currentStepInfo.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStepId])

  /**
   * Redirect to menu if there's no "stepName" that comes from URL, meaning it's just "/check-out" URL and previous step was "Account".
   * Why:
   * - initially user lands on "/check-out" URL, then user is getting redirected to "Account", meaning there would
   * always be "/check-out" entry in browser history.
   * Happens when:
   * - User is on "Account" step and clicks "Back" browser button, meaning user tries to go back to Menu (if previous
   * history entry is "/menu" from user perspective).
   */
  useEffect(() => {
    if (!urlStepId && previousUrlStepId === CheckoutStepIds.ACCOUNT) {
      dispatch(redirect(routesConfig.client.menu))
    }
  })

  const isDiscountAppliedBarVisible =
    isBoxDiscountVisualisationEnabled && isDiscountEnabled && !isDiscountAppliedBannerDismissed

  const handleDiscountAppliedBannerDismissal = () => {
    dispatch(trackUTMAndPromoCode(closeDiscountAppliedBar, { checkoutStep: urlStepId }))
    dispatch(signupDismissDiscountAppliedBar())
  }

  return (
    <>
      <ScreenReaderLiveRegion urgency="polite">
        {isDiscountAppliedBarVisible && (
          <DiscountAppliedBar
            onDismiss={handleDiscountAppliedBannerDismissal}
            boxDiscount={discountTip || ''}
          />
        )}
      </ScreenReaderLiveRegion>

      <Grid
        gap={4}
        paddingH={[0, null, 8]}
        paddingV={[0, null, 8]}
        paddingBottom={[0, 12]}
        data-testing="checkoutContainer"
        data-testid="checkoutContainer"
      >
        <Col size={12}>
          <Visible display={[Display.Block, Display.None]}>
            <BoxSummaryDrawer isCreatingPreviewOrder={false} />
          </Visible>
          <CheckoutUrgency />
          <Breadcrumbs
            currentBreadcrumb={currentStepInfo?.breadcrumb}
            breadcrumbsToStepsMapping={breadcrumbsToStepsMapping}
          />
        </Col>
        <Col paddingH={[0, 14, 20, 0]} size={[12, null, null, 7]}>
          <CheckoutStepContainer
            currentStepId={urlStepId}
            onStepChange={(stepId) => redirectToStep(stepId)}
            isCheckoutScriptReady={isCheckoutScriptReady}
            arePaypalScriptsReady={arePaypalScriptsReady}
            reloadCheckoutScript={loadCheckoutScript}
          />
        </Col>
        <Col paddingH={[0, 14, 20, 0]} size={[12, null, null, 5]}>
          <Visible display={[Display.None, Display.Block]}>
            <OrderSummaryAndBoxDetails isCreatingPreviewOrder={false} />
          </Visible>
        </Col>
        <LoginModal />
      </Grid>
    </>
  )
}
